import { API_BASE_URL } from "../../config/apiConfig";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ProfessionalPageNav from "../../components/professional/ProfessionalPageNav";
import PageLoader from "../../components/common/PageLoader";

import "../../styles/pages/professional/Availability.css";

const API_BASE = API_BASE_URL;

const EMPLOYMENT_TYPES = [
  {
    value: "travel-contract",
    label: "Travel Contract",
  },
  {
    value: "local-contract",
    label: "Local Contract",
  },
  {
    value: "per-diem",
    label: "Per Diem / Casual",
  },
  {
    value: "part-time",
    label: "Part-Time",
  },
  {
    value: "full-time",
    label: "Full-Time",
  },
];

const SHIFT_TYPES = [
  {
    value: "days",
    label: "Days",
  },
  {
    value: "evenings",
    label: "Evenings",
  },
  {
    value: "nights",
    label: "Nights",
  },
  {
    value: "rotating",
    label: "Rotating",
  },
];

const CANADIAN_PROVINCES = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Nova Scotia",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Northwest Territories",
  "Nunavut",
  "Yukon",
];

const createInitialForm = () => ({
  availabilityStatus:
    "available-on-date",

  currentlyWorking:
    "yes",

  currentAssignmentEndDate:
    "",

  earliestStartDate:
    "",

  noticePeriod:
    "two-weeks",

  startDateFlexibility:
    "within-one-week",

  employmentTypes:
    [],

  preferredShifts:
    [],

  preferredShiftLength:
    "12",

  preferredShiftsPerWeek:
    "3",

  minimumHoursPerWeek:
    "36",

  maximumHoursPerWeek:
    "48",

  availableWeekends:
    false,

  availableHolidays:
    false,

  willingToTakeCall:
    false,

  willingToWorkOvertime:
    false,

  willingToFloat:
    false,

  preferredProvinces:
    [],

  maximumTravelDistance:
    "100",

  willingToRelocate:
    false,

  willingToTravelOutsideProvince:
    false,

  minimumContractWeeks:
    "8",

  preferredContractWeeks:
    "13",

  maximumContractWeeks:
    "26",

  requestedTimeOff:
    "",

  schedulingNotes:
    "",
});


const getToken = () =>
  localStorage.getItem("zethAuthToken") ||
  sessionStorage.getItem("zethAuthToken");

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const normalizeDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const mapAvailabilityToForm = (availability) => ({
  availabilityStatus:
    availability.availability_status ||
    "available-on-date",

  currentlyWorking:
    availability.currently_working
      ? "yes"
      : "no",

  currentAssignmentEndDate:
    normalizeDate(
      availability.current_assignment_end_date
    ),

  earliestStartDate:
    normalizeDate(
      availability.earliest_start_date
    ),

  noticePeriod:
    availability.notice_period ||
    "two-weeks",

  startDateFlexibility:
    availability.start_date_flexibility ||
    "within-one-week",

  employmentTypes:
    normalizeArray(
      availability.employment_types
    ),

  preferredShifts:
    normalizeArray(
      availability.preferred_shifts
    ),

  preferredShiftLength:
    availability.preferred_shift_length ||
    "12",

  preferredShiftsPerWeek:
    availability.preferred_shifts_per_week != null
      ? String(
          availability.preferred_shifts_per_week
        )
      : "3",

  minimumHoursPerWeek:
    availability.minimum_hours_per_week != null
      ? String(
          availability.minimum_hours_per_week
        )
      : "36",

  maximumHoursPerWeek:
    availability.maximum_hours_per_week != null
      ? String(
          availability.maximum_hours_per_week
        )
      : "48",

  availableWeekends:
    Boolean(
      availability.available_weekends
    ),

  availableHolidays:
    Boolean(
      availability.available_holidays
    ),

  willingToTakeCall:
    Boolean(
      availability.willing_to_take_call
    ),

  willingToWorkOvertime:
    Boolean(
      availability.willing_to_work_overtime
    ),

  willingToFloat:
    Boolean(
      availability.willing_to_float
    ),

  preferredProvinces:
    normalizeArray(
      availability.preferred_provinces
    ),

  maximumTravelDistance:
    availability.maximum_travel_distance ||
    "100",

  willingToRelocate:
    Boolean(
      availability.willing_to_relocate
    ),

  willingToTravelOutsideProvince:
    Boolean(
      availability.willing_to_travel_outside_province
    ),

  minimumContractWeeks:
    availability.minimum_contract_weeks != null
      ? String(
          availability.minimum_contract_weeks
        )
      : "8",

  preferredContractWeeks:
    availability.preferred_contract_weeks != null
      ? String(
          availability.preferred_contract_weeks
        )
      : "13",

  maximumContractWeeks:
    availability.maximum_contract_weeks != null
      ? String(
          availability.maximum_contract_weeks
        )
      : "26",

  requestedTimeOff:
    availability.requested_time_off ||
    "",

  schedulingNotes:
    availability.scheduling_notes ||
    "",
});

function Availability() {
  const navigate = useNavigate();

  const [
    formData,
    setFormData,
  ] = useState(
    createInitialForm
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    messageType,
    setMessageType,
  ] = useState("success");

  const [
    isConfigured,
    setIsConfigured,
  ] = useState(false);

  useEffect(() => {
    const loadAvailability = async () => {
      const token = getToken();

      if (!token) {
        navigate(
          "/professional/login",
          { replace: true }
        );
        return;
      }

      try {
        setLoading(true);
        setMessage("");

        const response = await fetch(
          `${API_BASE}/professionals/availability`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (
          response.status === 401 ||
          response.status === 403
        ) {
          localStorage.removeItem(
            "zethAuthToken"
          );
          localStorage.removeItem(
            "zethUser"
          );
          sessionStorage.removeItem(
            "zethAuthToken"
          );
          sessionStorage.removeItem(
            "zethUser"
          );

          navigate(
            "/professional/login",
            { replace: true }
          );
          return;
        }

        if (response.status === 404) {
          setFormData(
            createInitialForm()
          );
          setIsConfigured(false);
          return;
        }

        const data = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to load availability."
          );
        }

        const savedAvailability =
          data.availability ||
          data.data ||
          null;

        if (savedAvailability) {
          setFormData(
            mapAvailabilityToForm(
              savedAvailability
            )
          );

          setIsConfigured(
            Boolean(
              savedAvailability.is_configured
            )
          );
        }
      } catch (error) {
        console.error(
          "Load availability error:",
          error
        );

        setMessageType("error");
        setMessage(
          error.message ||
            "Unable to load availability."
        );
      } finally {
        setLoading(false);
      }
    };

    loadAvailability();
  }, [navigate]);

  const selectedPreferenceCount =
    useMemo(() => {
      let count = 0;

      if (formData.earliestStartDate) {
        count += 1;
      }

      if (
        formData.employmentTypes.length > 0
      ) {
        count += 1;
      }

      if (
        formData.preferredShifts.length > 0
      ) {
        count += 1;
      }

      if (
        formData.preferredProvinces.length > 0
      ) {
        count += 1;
      }

      return count;
    }, [formData]);

  const handleChange = (event) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
      ...(
        name === "currentlyWorking" &&
        value === "no"
          ? {
              currentAssignmentEndDate:
                "",
            }
          : {}
      ),
    }));

    setMessage("");
  };

  const toggleArrayValue = (
    fieldName,
    value
  ) => {
    setFormData((current) => {
      const currentValues =
        Array.isArray(
          current[fieldName]
        )
          ? current[fieldName]
          : [];

      const alreadySelected =
        currentValues.includes(value);

      return {
        ...current,
        [fieldName]:
          alreadySelected
            ? currentValues.filter(
                (item) =>
                  item !== value
              )
            : [
                ...currentValues,
                value,
              ],
      };
    });

    setMessage("");
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    const token = getToken();

    if (!token) {
      navigate(
        "/professional/login",
        { replace: true }
      );
      return;
    }

    if (
      !formData.earliestStartDate
    ) {
      setMessageType("error");
      setMessage(
        "Earliest available start date is required."
      );
      return;
    }

    try {
      setIsSaving(true);
      setMessage("");

      const payload = {
        ...formData,
        currentlyWorking:
          formData.currentlyWorking ===
          "yes",
        currentAssignmentEndDate:
          formData.currentlyWorking ===
            "yes"
            ? (
                formData.currentAssignmentEndDate ||
                null
              )
            : null,
      };

      const response = await fetch(
        `${API_BASE}/professionals/availability`,
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
            Authorization:
              `Bearer ${token}`,
          },
          body: JSON.stringify(
            payload
          ),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        localStorage.removeItem(
          "zethAuthToken"
        );
        localStorage.removeItem(
          "zethUser"
        );
        sessionStorage.removeItem(
          "zethAuthToken"
        );
        sessionStorage.removeItem(
          "zethUser"
        );

        navigate(
          "/professional/login",
          { replace: true }
        );
        return;
      }

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to save availability."
        );
      }

      const savedAvailability =
        data.availability ||
        data.data ||
        null;

      if (savedAvailability) {
        setFormData(
          mapAvailabilityToForm(
            savedAvailability
          )
        );
      }

      setIsConfigured(true);
      setMessageType("success");
      setMessage(
        data.message ||
          "Availability saved successfully."
      );
    } catch (error) {
      console.error(
        "Save availability error:",
        error
      );

      setMessageType("error");
      setMessage(
        error.message ||
          "Unable to save availability."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLoader
        title="Availability"
        message="Loading your availability..."
      />
    );
  }

  return (
    <main className="availability-page">
      <div className="availability-page-nav">
        <ProfessionalPageNav />
      </div>

      <header className="availability-header">
        <div>
          <p className="account-label">
            Work Preferences
          </p>

          <h1>
            Availability
          </h1>

          <p>
            Tell us when and where you can
            work so we can match you with
            assignments that fit your
            schedule, preferred shifts, and
            travel plans.
          </p>
        </div>

        <div className="availability-status-summary">
          <span>
            Profile status
          </span>

          <strong>
            {isConfigured &&
            selectedPreferenceCount === 4
              ? "Configured"
              : "In Progress"}
          </strong>

          <small>
            {selectedPreferenceCount} of 4
            matching preferences completed
          </small>
        </div>
      </header>

      <form
        className="availability-form"
        onSubmit={handleSubmit}
      >
        <section className="availability-card">
          <div className="availability-card-header">
            <div>
              <span className="availability-step">
                01
              </span>

              <div>
                <h2>
                  Start Availability
                </h2>

                <p>
                  Let facilities know when
                  you can begin your next
                  assignment.
                </p>
              </div>
            </div>
          </div>

          <div className="availability-form-grid">
            <div className="availability-field">
              <label
                htmlFor="availabilityStatus"
              >
                Availability status
              </label>

              <select
                id="availabilityStatus"
                name="availabilityStatus"
                value={
                  formData.availabilityStatus
                }
                onChange={handleChange}
              >
                <option value="available-immediately">
                  Available immediately
                </option>

                <option value="available-on-date">
                  Available on a specific
                  date
                </option>

                <option value="after-current-assignment">
                  Available after current
                  assignment
                </option>

                <option value="not-currently-looking">
                  Not currently looking
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="currentlyWorking"
              >
                Currently working or on an
                assignment?
              </label>

              <select
                id="currentlyWorking"
                name="currentlyWorking"
                value={
                  formData.currentlyWorking
                }
                onChange={handleChange}
              >
                <option value="yes">
                  Yes
                </option>

                <option value="no">
                  No
                </option>
              </select>
            </div>

            {formData.currentlyWorking ===
              "yes" && (
              <div className="availability-field">
                <label
                  htmlFor="currentAssignmentEndDate"
                >
                  Current assignment end
                  date
                </label>

                <input
                  id="currentAssignmentEndDate"
                  name="currentAssignmentEndDate"
                  type="date"
                  value={
                    formData.currentAssignmentEndDate
                  }
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="availability-field">
              <label
                htmlFor="earliestStartDate"
              >
                Earliest available start
                date
              </label>

              <input
                id="earliestStartDate"
                name="earliestStartDate"
                type="date"
                value={
                  formData.earliestStartDate
                }
                onChange={handleChange}
                required
              />

              <small>
                Use the earliest date you
                could reasonably begin an
                assignment.
              </small>
            </div>

            <div className="availability-field">
              <label
                htmlFor="noticePeriod"
              >
                Required notice period
              </label>

              <select
                id="noticePeriod"
                name="noticePeriod"
                value={
                  formData.noticePeriod
                }
                onChange={handleChange}
              >
                <option value="none">
                  No notice required
                </option>

                <option value="one-week">
                  1 week
                </option>

                <option value="two-weeks">
                  2 weeks
                </option>

                <option value="three-weeks">
                  3 weeks
                </option>

                <option value="four-weeks">
                  4 weeks
                </option>

                <option value="more-than-four-weeks">
                  More than 4 weeks
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="startDateFlexibility"
              >
                Start-date flexibility
              </label>

              <select
                id="startDateFlexibility"
                name="startDateFlexibility"
                value={
                  formData.startDateFlexibility
                }
                onChange={handleChange}
              >
                <option value="exact-date-only">
                  Exact date only
                </option>

                <option value="within-one-week">
                  Flexible within 1 week
                </option>

                <option value="within-two-weeks">
                  Flexible within 2 weeks
                </option>

                <option value="very-flexible">
                  Very flexible
                </option>
              </select>
            </div>
          </div>
        </section>

        <section className="availability-card">
          <div className="availability-card-header">
            <div>
              <span className="availability-step">
                02
              </span>

              <div>
                <h2>
                  Assignment Preferences
                </h2>

                <p>
                  Select the types of work
                  you would like to receive
                  matches for.
                </p>
              </div>
            </div>
          </div>

          <div className="availability-choice-grid">
            {EMPLOYMENT_TYPES.map(
              (employmentType) => {
                const isSelected =
                  formData.employmentTypes.includes(
                    employmentType.value
                  );

                return (
                  <button
                    key={
                      employmentType.value
                    }
                    type="button"
                    className={`availability-choice ${
                      isSelected
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      toggleArrayValue(
                        "employmentTypes",
                        employmentType.value
                      )
                    }
                    aria-pressed={
                      isSelected
                    }
                  >
                    <span>
                      {
                        employmentType.label
                      }
                    </span>

                    <small>
                      {isSelected
                        ? "Selected"
                        : "Add preference"}
                    </small>
                  </button>
                );
              }
            )}
          </div>

          <div className="availability-form-grid availability-form-grid-three">
            <div className="availability-field">
              <label
                htmlFor="minimumContractWeeks"
              >
                Minimum contract length
              </label>

              <select
                id="minimumContractWeeks"
                name="minimumContractWeeks"
                value={
                  formData.minimumContractWeeks
                }
                onChange={handleChange}
              >
                <option value="4">
                  4 weeks
                </option>

                <option value="6">
                  6 weeks
                </option>

                <option value="8">
                  8 weeks
                </option>

                <option value="13">
                  13 weeks
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="preferredContractWeeks"
              >
                Preferred contract length
              </label>

              <select
                id="preferredContractWeeks"
                name="preferredContractWeeks"
                value={
                  formData.preferredContractWeeks
                }
                onChange={handleChange}
              >
                <option value="8">
                  8 weeks
                </option>

                <option value="13">
                  13 weeks
                </option>

                <option value="16">
                  16 weeks
                </option>

                <option value="26">
                  26 weeks
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="maximumContractWeeks"
              >
                Maximum contract length
              </label>

              <select
                id="maximumContractWeeks"
                name="maximumContractWeeks"
                value={
                  formData.maximumContractWeeks
                }
                onChange={handleChange}
              >
                <option value="13">
                  13 weeks
                </option>

                <option value="16">
                  16 weeks
                </option>

                <option value="26">
                  26 weeks
                </option>

                <option value="52">
                  Up to 1 year
                </option>
              </select>
            </div>
          </div>
        </section>

        <section className="availability-card">
          <div className="availability-card-header">
            <div>
              <span className="availability-step">
                03
              </span>

              <div>
                <h2>
                  Shift Preferences
                </h2>

                <p>
                  Choose the schedules you
                  are comfortable working.
                </p>
              </div>
            </div>
          </div>

          <div className="availability-choice-grid">
            {SHIFT_TYPES.map(
              (shiftType) => {
                const isSelected =
                  formData.preferredShifts.includes(
                    shiftType.value
                  );

                return (
                  <button
                    key={
                      shiftType.value
                    }
                    type="button"
                    className={`availability-choice ${
                      isSelected
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      toggleArrayValue(
                        "preferredShifts",
                        shiftType.value
                      )
                    }
                    aria-pressed={
                      isSelected
                    }
                  >
                    <span>
                      {shiftType.label}
                    </span>

                    <small>
                      {isSelected
                        ? "Selected"
                        : "Available"}
                    </small>
                  </button>
                );
              }
            )}
          </div>

          <div className="availability-form-grid availability-form-grid-three">
            <div className="availability-field">
              <label
                htmlFor="preferredShiftLength"
              >
                Preferred shift length
              </label>

              <select
                id="preferredShiftLength"
                name="preferredShiftLength"
                value={
                  formData.preferredShiftLength
                }
                onChange={handleChange}
              >
                <option value="8">
                  8 hours
                </option>

                <option value="10">
                  10 hours
                </option>

                <option value="12">
                  12 hours
                </option>

                <option value="flexible">
                  Flexible
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="preferredShiftsPerWeek"
              >
                Preferred shifts per week
              </label>

              <select
                id="preferredShiftsPerWeek"
                name="preferredShiftsPerWeek"
                value={
                  formData.preferredShiftsPerWeek
                }
                onChange={handleChange}
              >
                <option value="1">
                  1 shift
                </option>

                <option value="2">
                  2 shifts
                </option>

                <option value="3">
                  3 shifts
                </option>

                <option value="4">
                  4 shifts
                </option>

                <option value="5">
                  5 shifts
                </option>
              </select>
            </div>

            <div className="availability-field">
              <label
                htmlFor="minimumHoursPerWeek"
              >
                Minimum hours per week
              </label>

              <input
                id="minimumHoursPerWeek"
                name="minimumHoursPerWeek"
                type="number"
                min="0"
                max="80"
                value={
                  formData.minimumHoursPerWeek
                }
                onChange={handleChange}
              />
            </div>

            <div className="availability-field">
              <label
                htmlFor="maximumHoursPerWeek"
              >
                Maximum hours per week
              </label>

              <input
                id="maximumHoursPerWeek"
                name="maximumHoursPerWeek"
                type="number"
                min="0"
                max="100"
                value={
                  formData.maximumHoursPerWeek
                }
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="availability-toggle-grid">
            <label className="availability-toggle">
              <input
                name="availableWeekends"
                type="checkbox"
                checked={
                  formData.availableWeekends
                }
                onChange={handleChange}
              />

              <span>
                Available weekends
              </span>
            </label>

            <label className="availability-toggle">
              <input
                name="availableHolidays"
                type="checkbox"
                checked={
                  formData.availableHolidays
                }
                onChange={handleChange}
              />

              <span>
                Available holidays
              </span>
            </label>

            <label className="availability-toggle">
              <input
                name="willingToTakeCall"
                type="checkbox"
                checked={
                  formData.willingToTakeCall
                }
                onChange={handleChange}
              />

              <span>
                Willing to take call
              </span>
            </label>

            <label className="availability-toggle">
              <input
                name="willingToWorkOvertime"
                type="checkbox"
                checked={
                  formData.willingToWorkOvertime
                }
                onChange={handleChange}
              />

              <span>
                Available for overtime
              </span>
            </label>

            <label className="availability-toggle">
              <input
                name="willingToFloat"
                type="checkbox"
                checked={
                  formData.willingToFloat
                }
                onChange={handleChange}
              />

              <span>
                Willing to float between
                units
              </span>
            </label>
          </div>
        </section>

        <section className="availability-card">
          <div className="availability-card-header">
            <div>
              <span className="availability-step">
                04
              </span>

              <div>
                <h2>
                  Location Preferences
                </h2>

                <p>
                  Select the provinces and
                  travel arrangements you
                  are open to.
                </p>
              </div>
            </div>
          </div>

          <div className="availability-province-grid">
            {CANADIAN_PROVINCES.map(
              (province) => {
                const isSelected =
                  formData.preferredProvinces.includes(
                    province
                  );

                return (
                  <label
                    key={province}
                    className={`availability-province ${
                      isSelected
                        ? "selected"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={
                        isSelected
                      }
                      onChange={() =>
                        toggleArrayValue(
                          "preferredProvinces",
                          province
                        )
                      }
                    />

                    <span>
                      {province}
                    </span>
                  </label>
                );
              }
            )}
          </div>

          <div className="availability-form-grid">
            <div className="availability-field">
              <label
                htmlFor="maximumTravelDistance"
              >
                Maximum travel distance
              </label>

              <select
                id="maximumTravelDistance"
                name="maximumTravelDistance"
                value={
                  formData.maximumTravelDistance
                }
                onChange={handleChange}
              >
                <option value="25">
                  Up to 25 km
                </option>

                <option value="50">
                  Up to 50 km
                </option>

                <option value="100">
                  Up to 100 km
                </option>

                <option value="250">
                  Up to 250 km
                </option>

                <option value="unlimited">
                  No distance restriction
                </option>
              </select>
            </div>
          </div>

          <div className="availability-toggle-grid">
            <label className="availability-toggle">
              <input
                name="willingToRelocate"
                type="checkbox"
                checked={
                  formData.willingToRelocate
                }
                onChange={handleChange}
              />

              <span>
                Willing to relocate
              </span>
            </label>

            <label className="availability-toggle">
              <input
                name="willingToTravelOutsideProvince"
                type="checkbox"
                checked={
                  formData.willingToTravelOutsideProvince
                }
                onChange={handleChange}
              />

              <span>
                Willing to work outside my
                home province
              </span>
            </label>
          </div>
        </section>

        <section className="availability-card">
          <div className="availability-card-header">
            <div>
              <span className="availability-step">
                05
              </span>

              <div>
                <h2>
                  Scheduling Notes
                </h2>

                <p>
                  Add requested time off or
                  other information that may
                  affect your availability.
                </p>
              </div>
            </div>
          </div>

          <div className="availability-form-grid">
            <div className="availability-field availability-field-full">
              <label
                htmlFor="requestedTimeOff"
              >
                Requested time off
              </label>

              <textarea
                id="requestedTimeOff"
                name="requestedTimeOff"
                rows="4"
                value={
                  formData.requestedTimeOff
                }
                onChange={handleChange}
                placeholder="Example: Unavailable December 22–28, 2026."
              />

              <small>
                Include known vacation,
                family, education, or
                appointment dates.
              </small>
            </div>

            <div className="availability-field availability-field-full">
              <label
                htmlFor="schedulingNotes"
              >
                Additional availability
                notes
              </label>

              <textarea
                id="schedulingNotes"
                name="schedulingNotes"
                rows="5"
                value={
                  formData.schedulingNotes
                }
                onChange={handleChange}
                placeholder="Add any scheduling limitations, preferences, or flexibility."
              />
            </div>
          </div>
        </section>

        {message && (
          <div
            className={`availability-message ${messageType === "error" ? "error" : ""}`}
            role="status"
          >
            {message}
          </div>
        )}

        <div className="availability-save-container">
          <div>
            <strong>
              Ready to update your matches?
            </strong>

            <span>
              Your preferences will be used
              to identify suitable
              assignments.
            </span>
          </div>

          <button
            type="submit"
            className="availability-save-button"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : "Save Availability"}
          </button>
        </div>
      </form>
    </main>
  );
}

export default Availability;
